/**
 * Moodshot Manager — templates for putting the 3D product on a mood photo.
 *
 * Everything that shapes the picture is a slider and is stored per template:
 * where the product stands (one or more spots), the scale in px per mm, the
 * view angle, the shadow (drawn live here with the same formula the server
 * uses), the grade on the product and the filter over the whole picture.
 * Changes are saved automatically; the real render is made by the modal (or by
 * POST /api/moodshots/maak) with the product taken from the 3D viewer.
 */
import './style.css';

const API = '/api/moodshots';

interface Sjabloon {
  id: number | string;
  naam: string;
  bestand: string;
  breedte_px?: number | string;
  hoogte_px?: number | string;
  anker_x: number | string;
  anker_y: number | string;
  px_per_mm: number | string;
  hoek?: number | string;
  plekken?: string | Plek[] | null;
  filter?: string;
  voorbeeld_sku?: string | null;
  [k: string]: unknown;
}
interface Plek { x: number; y: number; px_per_mm: number; hoek: number }
interface Product { hoogte_mm: number; naam: string; knipsel: string; knipsel_breedte: number; knipsel_hoogte: number }
interface Knipsel { url: string; verh: number }
interface Project { id: number | string; naam: string; template_id: number | string; sku?: string; instellingen: string | Record<string, unknown> }

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`element #${id} ontbreekt`);
  return el as T;
};
const inp = (id: string): HTMLInputElement => $<HTMLInputElement>(id);
const num = (id: string, standaard = 0): number => { const n = Number(inp(id).value); return Number.isFinite(n) ? n : standaard; };

// template-wide sliders (stored per template)
const VELDEN = ['hoogtehoek', 'schaduw_hoek', 'schaduw_lengte', 'schaduw_breedte', 'schaduw_dy', 'schaduw_sterkte', 'schaduw_zacht',
  'schaduw_x', 'schaduw_y', 'schaduw_flare', 'schaduw_tip_zacht', 'schaduw_vervaag',
  'product_warmte', 'product_licht', 'eind_warmte', 'korrel', 'vignet'] as const;
// sliders of the active spot
const PLEKVELDEN = ['x', 'y', 'px_per_mm', 'hoek'] as const;
const DECIMALEN: Record<string, number> = { x: 0, y: 0, hoek: 0, hoogtehoek: 0, schaduw_zacht: 0, schaduw_hoek: 0, schaduw_x: 0, schaduw_y: 0, px_per_mm: 2 };
const STANDAARD: Record<string, number> = { hoogtehoek: 0, schaduw_hoek: 0, schaduw_lengte: 0.6, schaduw_breedte: 1, schaduw_dy: 0.15,
  schaduw_sterkte: 0.45, schaduw_zacht: 8, schaduw_x: 0, schaduw_y: 0,
  schaduw_flare: 0, schaduw_tip_zacht: 0, schaduw_vervaag: 0,
  product_warmte: 0.04, product_licht: 0.12, eind_warmte: 0.08, korrel: 0.06, vignet: 0.12 };

let sjablonen: Sjabloon[] = [];
let huidig: Sjabloon | null = null;
let plekken: Plek[] = [];
let actief = 0;
let projecten: Project[] = [];
let product: Product | null = null;
let renders: Record<string, string> = {};      // key hoek|hoogtehoek -> dataUrl (800x600 render)
let knipsels: Record<string, Knipsel> = {};   // same key -> cropped product for the canvas
let viewerKlaar = false;
let viewerSku = '';

function toon(tekst: string, fout = false): void {
  const m = $('melding'); m.textContent = tekst; m.classList.toggle('fout', fout);
}
const fmt = (v: unknown, d?: number): string => Number(v).toFixed(d === undefined ? 2 : d);
const sleutel = (hoek: number): string => `${Number(hoek) || 0}|${num('hoogtehoek')}`;

// ---------- 3D viewer -----------------------------------------------------------
function viewerVenster(): Window | null { return ($('viewer') as HTMLIFrameElement).contentWindow; }
function laadViewer(sku: string): void {
  if (viewerSku === sku) return;
  viewerSku = sku; viewerKlaar = false; renders = {}; knipsels = {};
  ($('viewer') as HTMLIFrameElement).src = `/3d/?sku=${encodeURIComponent(sku)}&embed=true&cap=1&nocache=${Date.now()}`;
}
// In embed mode the model stays hidden until the parent sends something; an
// empty wrap is enough to show it. Without this every render is blank.
function wekViewer(): void { try { viewerVenster()?.postMessage({ type: 'hg_wrap', dataUrl: '' }, '*'); } catch { /* none */ } }
window.addEventListener('message', (e: MessageEvent) => {
  const d = e.data || {};
  if (d.type === 'magic_3d_ready') { wekViewer(); setTimeout(() => { viewerKlaar = true; }, 700); }
});
function vraagRender(hoek: number, hoogteHoek: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const venster = viewerVenster();
    if (!venster) return reject(new Error('no viewer'));
    const fn = (e: MessageEvent) => {
      const d = e.data || {};
      if (d.type === 'product_png_response' && Number(d.hoek || 0) === hoek && Number(d.hoogteHoek || 0) === hoogteHoek) {
        window.removeEventListener('message', fn); resolve(String(d.dataUrl));
      }
    };
    window.addEventListener('message', fn);
    venster.postMessage({ type: 'request_product_png', hoek, hoogteHoek }, '*');
    setTimeout(() => { window.removeEventListener('message', fn); reject(new Error('3D viewer is not responding')); }, 12000);
  });
}
async function laadBeeld(dataUrl: string): Promise<HTMLImageElement> {
  const img = new Image(); img.src = dataUrl; await img.decode(); return img;
}
async function heeftInhoud(dataUrl: string): Promise<boolean> {
  const img = await laadBeeld(dataUrl);
  const c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
  const ctx = c.getContext('2d')!; ctx.drawImage(img, 0, 0);
  const d = ctx.getImageData(0, 0, c.width, c.height).data;
  let n = 0;
  for (let i = 3; i < d.length; i += 28) { if (d[i] > 8 && ++n > 200) return true; }
  return false;
}
async function render(hoek: number): Promise<string> {
  const k = sleutel(hoek);
  if (renders[k]) return renders[k];
  const hoogte = num('hoogtehoek');
  for (let poging = 0; poging < 4; poging++) {
    const d = await vraagRender(Number(hoek) || 0, hoogte);
    if (await heeftInhoud(d)) { renders[k] = d; await knip(k, d); return d; }
    wekViewer();
    await new Promise((r) => setTimeout(r, 900));
  }
  throw new Error('3D viewer returned an empty image');
}
// crop to the product for the canvas; the aspect ratio comes with it
async function knip(k: string, dataUrl: string): Promise<void> {
  const img = await laadBeeld(dataUrl);
  const c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
  const ctx = c.getContext('2d')!; ctx.drawImage(img, 0, 0);
  const d = ctx.getImageData(0, 0, c.width, c.height).data;
  let minX = c.width, maxX = 0, minY = c.height, maxY = 0;
  for (let y = 0; y < c.height; y++) for (let x = 0; x < c.width; x++) {
    if (d[(y * c.width + x) * 4 + 3] > 8) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
  }
  if (maxX <= minX || maxY <= minY) return;
  const kc = document.createElement('canvas'); kc.width = maxX - minX + 1; kc.height = maxY - minY + 1;
  kc.getContext('2d')!.drawImage(c, minX, minY, kc.width, kc.height, 0, 0, kc.width, kc.height);
  knipsels[k] = { url: kc.toDataURL('image/png'), verh: kc.width / kc.height };
}
// while the view angle slides the new render is not there yet: keep the crop of
// the nearest angle instead of falling back to the photo
function dichtstbij(hoek: number): Knipsel | null {
  const hh = String(num('hoogtehoek'));
  let beste: Knipsel | null = null, afstand = 1e9;
  for (const k of Object.keys(knipsels)) {
    const [h, hoogte] = k.split('|');
    if (hoogte !== hh) continue;
    const d = Math.abs(Number(h) - hoek);
    if (d < afstand) { afstand = d; beste = knipsels[k]; }
  }
  if (!beste) { const eerste = Object.keys(knipsels)[0]; if (eerste) beste = knipsels[eerste]; }
  return beste;
}

// ---------- drawing ---------------------------------------------------------------
const achtergrond = (): HTMLImageElement => $('achtergrond') as HTMLImageElement;
const doek = (): HTMLDivElement => $('doek') as HTMLDivElement;
function factor(): number { const a = achtergrond(); return a.naturalWidth ? a.clientWidth / a.naturalWidth : 1; }

// live filter on the whole canvas (background, shadow, product, logo); the server does the same over the final image
function liveFilter(): void {
  const stijl = (inp('filter') as unknown as HTMLSelectElement).value, kleur = num('eind_warmte'), vig = num('vignet');
  let css = `saturate(0.92) contrast(1.06) sepia(${kleur.toFixed(2)})`;
  if (stijl === 'warm') css = `saturate(0.94) contrast(1.04) sepia(${(0.25 + kleur).toFixed(2)})`;
  else if (stijl === 'koel') css = 'saturate(0.9) contrast(1.06) hue-rotate(-8deg) brightness(1.02)';
  else if (stijl === 'mat') css = 'saturate(0.85) contrast(0.92) brightness(1.04)';
  else if (stijl === 'contrast') css = 'saturate(1.04) contrast(1.16)';
  else if (stijl === 'zwartwit') css = 'grayscale(1) contrast(1.08)';
  doek().style.filter = css;
  let v = doek().querySelector<HTMLDivElement>('.vignet');
  if (!v) { v = document.createElement('div'); v.className = 'vignet'; doek().appendChild(v); }
  v.style.background = `radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, rgba(0,0,0,${(vig * 0.9).toFixed(2)}) 100%)`;
}

type Punt = [number, number];
/**
 * CSS matrix3d that maps four source points onto four target points (a homography).
 * Used for the flared shadow: the tip is wider than the foot, which a plain 2D
 * matrix cannot do. Returns null when the points are degenerate.
 */
function homografie(bron: Punt[], doel: Punt[]): string | null {
  // x' = (h0 x + h1 y + h2) / (h6 x + h7 y + 1),  y' = (h3 x + h4 y + h5) / (h6 x + h7 y + 1)
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = bron[i];
    const [u, v] = doel[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  const n = 8;
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
    if (Math.abs(A[p][c]) < 1e-10) return null;
    [A[c], A[p]] = [A[p], A[c]];
    [b[c], b[p]] = [b[p], b[c]];
    for (let r = c + 1; r < n; r++) {
      const q = A[r][c] / A[c][c];
      for (let kk = c; kk < n; kk++) A[r][kk] -= q * A[c][kk];
      b[r] -= q * b[c];
    }
  }
  const h: number[] = new Array(n).fill(0);
  for (let r = n - 1; r >= 0; r--) {
    let som = b[r];
    for (let kk = r + 1; kk < n; kk++) som -= A[r][kk] * h[kk];
    h[r] = som / A[r][r];
  }
  if (!h.every((v) => Number.isFinite(v))) return null;
  // every corner must stay in front of the "camera", otherwise the browser flips or hides it
  for (const [x, y] of bron) if (h[6] * x + h[7] * y + 1 <= 1e-6) return null;
  const m = [h[0], h[3], 0, h[6], h[1], h[4], 0, h[7], 0, 0, 1, 0, h[2], h[5], 0, 1];
  return `matrix3d(${m.map((v) => Number(v.toPrecision(12))).join(',')})`;
}

function teken(): void {
  if (!huidig) return;
  doek().querySelectorAll('.product, .anker, .schaduw, .vignet').forEach((el) => el.remove());
  liveFilter();
  const f = factor();
  plekken.forEach((p, i) => {
    const hoogteMm = product?.hoogte_mm || 100;
    const hoogtePx = hoogteMm * p.px_per_mm;
    const kn = knipsels[sleutel(p.hoek)] || dichtstbij(p.hoek);
    const beeld = kn ? kn.url : (product?.knipsel || '');
    const verh = kn ? kn.verh : (product ? product.knipsel_breedte / product.knipsel_hoogte : 0.35);
    const breedtePx = hoogtePx * verh;

    // live shadow: same mapping as the server. The silhouette is anchored at the foot,
    // stretched along the angle (length), widened across (width), squashed by flatness.
    // Flare makes the tip (1 + flare) times as wide as the foot, which needs a perspective
    // transform; tip softness and tip fade blend a sharp and a soft layer towards the tip.
    if (beeld) {
      const th = (num('schaduw_hoek') * Math.PI) / 180;
      const lengte = Math.max(0.01, num('schaduw_lengte')), br = num('schaduw_breedte', 1) || 1;
      const plat = Math.max(0.05, num('schaduw_dy'));
      const flare = Math.max(0, num('schaduw_flare')), tipZacht = Math.max(0, num('schaduw_tip_zacht'));
      const vervaag = Math.min(1, Math.max(0, num('schaduw_vervaag')));
      const vx = num('schaduw_x') * f, vy = num('schaduw_y') * f;
      const W = breedtePx * f, H = hoogtePx * f;
      const basis = (num('schaduw_zacht', 8) || 8) * 0.6 * f;
      const tipBlur = basis * (1 + tipZacht);
      const P = Math.ceil(3 * tipBlur) + 2;                 // margin, so the masks do not clip the blur
      const Bx = P + W / 2, By = P + H;                     // foot centre
      const dx = lengte * H * Math.cos(th), dy = -lengte * H * Math.sin(th) * plat;          // foot -> tip
      const ax = -br * (W / 2) * Math.sin(th), ay = -br * (W / 2) * Math.cos(th) * plat;     // centre -> right edge
      const k = 1 + flare;
      const m3 = homografie(
        [[P, P + H], [P + W, P + H], [P, P], [P + W, P]],
        [[Bx - ax, By - ay], [Bx + ax, By + ay], [Bx + dx - k * ax, By + dy - k * ay], [Bx + dx + k * ax, By + dy + k * ay]]);
      if (m3) {
        const sch = document.createElement('div'); sch.className = 'schaduw';
        sch.style.width = `${W + 2 * P}px`; sch.style.height = `${H + 2 * P}px`;
        sch.style.left = `${(p.x - breedtePx / 2) * f - P}px`; sch.style.top = `${(p.y - hoogtePx) * f - P}px`;
        sch.style.transform = `translate(${vx}px, ${vy}px) ${m3}`;
        sch.style.opacity = String(num('schaduw_sterkte'));
        const zetMasker = (el: HTMLElement, masker: string): void => {
          el.style.setProperty('-webkit-mask-image', masker);
          el.style.setProperty('mask-image', masker);
        };
        // gradients run from the bottom edge upwards: the foot is P px in, the tip P + H px
        if (vervaag > 0.01) {
          zetMasker(sch, `linear-gradient(to top, rgba(0,0,0,1) ${P}px, rgba(0,0,0,${(1 - vervaag).toFixed(3)}) ${P + H}px)`);
        }
        const laag = (blur: number, masker: string | null): void => {
          const l = document.createElement('div'); l.className = 'laag';
          l.style.filter = `blur(${blur.toFixed(2)}px)`;
          if (masker) zetMasker(l, masker);
          const si = document.createElement('img'); si.src = beeld;
          si.style.left = `${P}px`; si.style.top = `${P}px`; si.style.width = `${W}px`; si.style.height = `${H}px`;
          l.appendChild(si); sch.appendChild(l);
        };
        if (tipZacht > 0.01) {
          laag(basis, `linear-gradient(to top, rgba(0,0,0,1) ${P}px, rgba(0,0,0,0) ${P + H}px)`);
          laag(tipBlur, `linear-gradient(to top, rgba(0,0,0,0) ${P}px, rgba(0,0,0,1) ${P + H}px)`);
        } else {
          laag(basis, null);
        }
        doek().appendChild(sch);
      }
    }

    const el = document.createElement('div');
    el.className = 'product' + (i === actief ? ' actief' : '');
    el.style.width = `${breedtePx * f}px`; el.style.height = `${hoogtePx * f}px`;
    el.style.left = `${(p.x - breedtePx / 2) * f}px`; el.style.top = `${(p.y - hoogtePx) * f}px`;
    el.dataset.i = String(i);
    if (beeld) {
      const img = document.createElement('img'); img.src = beeld;
      const w = num('product_warmte'), licht = num('product_licht');
      img.style.filter = `sepia(${(w * 1.6).toFixed(2)}) saturate(${(1 + w * 0.6).toFixed(2)})`;
      el.appendChild(img);
      if (licht > 0.001) {
        // side light: gradient from light (left) to dark (right), only on the product via a mask
        const lv = document.createElement('div');
        lv.style.cssText = 'position:absolute;inset:0;pointer-events:none;mix-blend-mode:soft-light;'
          + `background:linear-gradient(to right, rgba(255,255,255,${(licht * 0.9).toFixed(2)}), rgba(0,0,0,${(licht * 0.9).toFixed(2)}));`
          + `-webkit-mask-image:url(${beeld});mask-image:url(${beeld});-webkit-mask-size:100% 100%;mask-size:100% 100%;`;
        el.appendChild(lv);
      }
    }
    el.addEventListener('mousedown', startSleep);
    doek().appendChild(el);
    const a = document.createElement('div'); a.className = 'anker';
    a.style.left = `${p.x * f}px`; a.style.top = `${p.y * f}px`;
    doek().appendChild(a);
  });
  const vig = doek().querySelector('.vignet'); if (vig) doek().appendChild(vig);
  const p = plekken[actief];
  if (p) PLEKVELDEN.forEach((v) => { inp(v).value = String(p[v]); $(v + '-w').textContent = fmt(p[v], DECIMALEN[v]); });
  $('plekken').innerHTML = plekken.map((q, i) => `<span class="plek ${i === actief ? 'actief' : ''}" data-i="${i}">spot ${i + 1} · ${q.hoek}°</span>`).join('');
  $('plekken').querySelectorAll<HTMLElement>('.plek').forEach((el) => el.addEventListener('click', () => { actief = Number(el.dataset.i); teken(); }));
}

// ---------- dragging --------------------------------------------------------------
let sleep: { x0: number; y0: number; px: number; py: number } | null = null;
function startSleep(e: MouseEvent): void {
  actief = Number((e.currentTarget as HTMLElement).dataset.i);
  const p = plekken[actief];
  sleep = { x0: e.clientX, y0: e.clientY, px: p.x, py: p.y };
  e.preventDefault(); teken();
}
window.addEventListener('mousemove', (e) => {
  if (!sleep) return;
  const f = factor(); const p = plekken[actief];
  p.x = Math.round(sleep.px + (e.clientX - sleep.x0) / f);
  p.y = Math.round(sleep.py + (e.clientY - sleep.y0) / f);
  teken();
});
window.addEventListener('mouseup', () => { if (sleep) planOpslaan(); sleep = null; }, true);

// ---------- templates -------------------------------------------------------------
async function laadSjablonen(kiesId?: number | string): Promise<void> {
  const d = await fetch(`${API}/sjablonen`).then((r) => r.json());
  sjablonen = d.sjablonen || [];
  const sel = $('sjabloon') as HTMLSelectElement;
  sel.innerHTML = sjablonen.map((s) => `<option value="${s.id}">${s.naam}</option>`).join('');
  if (kiesId !== undefined) sel.value = String(kiesId);
  kiesSjabloon();
}
function zetVelden(bron: Record<string, unknown>): void {
  VELDEN.forEach((v) => {
    const el = inp(v);
    if (bron[v] !== undefined && bron[v] !== null) el.value = String(bron[v]);
    $(v + '-w').textContent = fmt(el.value, DECIMALEN[v]);
  });
  ($('filter') as HTMLSelectElement).value = String(bron.filter || 'neutraal');
}
function leesPlekken(s: Sjabloon): Plek[] {
  let lijst: unknown = null;
  try { lijst = typeof s.plekken === 'string' ? JSON.parse(s.plekken) : s.plekken; } catch { lijst = null; }
  if (Array.isArray(lijst) && lijst.length) {
    return lijst.map((p: Record<string, unknown>) => ({ x: Number(p.x), y: Number(p.y), px_per_mm: Number(p.px_per_mm ?? s.px_per_mm), hoek: Number(p.hoek || 0) }));
  }
  return [{ x: Number(s.anker_x), y: Number(s.anker_y), px_per_mm: Number(s.px_per_mm), hoek: Number(s.hoek || 0) }];
}
function kiesSjabloon(): void {
  const sel = $('sjabloon') as HTMLSelectElement;
  huidig = sjablonen.find((s) => String(s.id) === sel.value) || sjablonen[0] || null;
  if (!huidig) return;
  achtergrond().src = `/moodshots/${huidig.bestand}?t=${Date.now()}`;
  inp('x').max = String(huidig.breedte_px || 1536); inp('y').max = String(huidig.hoogte_px || 1024);
  plekken = leesPlekken(huidig);
  actief = 0;
  zetVelden(huidig);
  renders = {}; knipsels = {};
  teken(); void ververs();
  // the article stored with this template
  if (huidig.voorbeeld_sku && huidig.voorbeeld_sku !== inp('sku').value.trim()) {
    inp('sku').value = huidig.voorbeeld_sku;
    void laadProduct();
  }
}
async function ververs(): Promise<void> {
  if (!product || !viewerKlaar) return;
  for (const p of plekken) { try { await render(p.hoek); } catch { /* photo stays */ } }
  teken();
}
async function laadProduct(): Promise<void> {
  const sku = inp('sku').value.trim();
  toon('loading…');
  try {
    const d = await fetch(`${API}/product?sku=${encodeURIComponent(sku)}`).then((r) => r.json());
    if (!d.success) { toon(d.error, true); return; }
    product = d as Product;
    $('kop-sku').textContent = sku; $('kop-naam').textContent = d.naam || '';
    $('hoogte').textContent = `${d.hoogte_mm || '?'} mm tall`;
    laadViewer(sku);
    toon('waiting for 3D…');
    const start = Date.now();
    while (!viewerKlaar && Date.now() - start < 20000) await new Promise((r) => setTimeout(r, 300));
    await ververs();
    toon(viewerKlaar ? 'product loaded from 3D' : '3D viewer not ready; showing the photo instead', !viewerKlaar);
  } catch (e) { toon(String(e), true); }
}

// ---------- sliders ---------------------------------------------------------------
VELDEN.forEach((v) => inp(v).addEventListener('input', () => {
  $(v + '-w').textContent = fmt(inp(v).value, DECIMALEN[v]);
  if (v === 'hoogtehoek') { renders = {}; knipsels = {}; }
  teken();
}));
inp('hoogtehoek').addEventListener('change', () => void ververs());
PLEKVELDEN.forEach((v) => inp(v).addEventListener('input', () => {
  const p = plekken[actief]; if (!p) return;
  p[v] = Number(inp(v).value); $(v + '-w').textContent = fmt(p[v], DECIMALEN[v]); teken();
}));
inp('hoek').addEventListener('change', () => void ververs());
function zetHoek(w: number): void {
  const p = plekken[actief]; if (!p) return;
  let h = Math.round(w); h = ((h % 360) + 360) % 360;
  p.hoek = h; inp('hoek').value = String(h); teken(); void ververs(); planOpslaan();
}
$('hoek-min').addEventListener('click', () => zetHoek(num('hoek') - 45));
$('hoek-nul').addEventListener('click', () => zetHoek(0));
$('hoek-plus').addEventListener('click', () => zetHoek(num('hoek') + 45));
$('plek-erbij').addEventListener('click', () => {
  const p = plekken[actief] || { x: 700, y: 800, px_per_mm: 2.3, hoek: 0 };
  plekken.push({ x: Math.min(Number(inp('x').max), p.x + 260), y: p.y, px_per_mm: p.px_per_mm, hoek: 0 });
  actief = plekken.length - 1; teken(); void ververs(); planOpslaan();
});
$('plek-weg').addEventListener('click', () => {
  if (plekken.length <= 1) return;
  plekken.splice(actief, 1); actief = Math.max(0, actief - 1); teken(); planOpslaan();
});
$('sjabloon').addEventListener('change', kiesSjabloon);
$('laad').addEventListener('click', () => void laadProduct());
achtergrond().addEventListener('load', teken);
window.addEventListener('resize', teken);
$('filter').addEventListener('change', () => { teken(); planOpslaan(); });
$('reset').addEventListener('click', () => {
  Object.keys(STANDAARD).forEach((v) => { inp(v).value = String(STANDAARD[v]); $(v + '-w').textContent = fmt(inp(v).value, DECIMALEN[v]); });
  ($('filter') as HTMLSelectElement).value = 'neutraal';
  renders = {}; knipsels = {}; teken(); void ververs(); planOpslaan();
  toon('defaults restored');
});

// minus/plus at either side of every slider; steps with the slider's own step
document.querySelectorAll<HTMLInputElement>('input[type=range]').forEach((r) => {
  const vak = document.createElement('div'); vak.className = 'schuif';
  r.parentNode!.insertBefore(vak, r);
  const maak = (teken2: string, richting: number) => {
    const b = document.createElement('button'); b.type = 'button'; b.className = 'stap'; b.textContent = teken2;
    b.addEventListener('click', () => {
      const stap = Number(r.step) || 1;
      const nieuw = Math.min(Number(r.max), Math.max(Number(r.min), Number(r.value) + richting * stap));
      r.value = String(Math.round(nieuw / stap) * stap);
      r.dispatchEvent(new Event('input', { bubbles: true }));
      r.dispatchEvent(new Event('change', { bubbles: true }));
    });
    return b;
  };
  vak.appendChild(maak('−', -1)); vak.appendChild(r); vak.appendChild(maak('+', 1));
});

// ---------- saving ----------------------------------------------------------------
function instellingen(): Record<string, unknown> {
  const uit: Record<string, unknown> = {};
  VELDEN.forEach((v) => { uit[v] = num(v); });
  uit.filter = ($('filter') as HTMLSelectElement).value;
  uit.voorbeeld_sku = inp('sku').value.trim();
  const p0 = plekken[0];
  uit.anker_x = p0.x; uit.anker_y = p0.y; uit.px_per_mm = p0.px_per_mm; uit.hoek = p0.hoek;
  uit.plekken = plekken.length > 1 ? plekken.map((p) => ({ x: p.x, y: p.y, px_per_mm: p.px_per_mm, hoek: p.hoek })) : [];
  return uit;
}
function pasToe(inst: Record<string, unknown>): void {
  zetVelden(inst);
  const lijst = inst.plekken;
  if (Array.isArray(lijst) && lijst.length) {
    plekken = lijst.map((p: Record<string, unknown>) => ({ x: Number(p.x), y: Number(p.y), px_per_mm: Number(p.px_per_mm), hoek: Number(p.hoek || 0) }));
  } else {
    plekken = [{ x: Number(inst.anker_x), y: Number(inst.anker_y), px_per_mm: Number(inst.px_per_mm), hoek: Number(inst.hoek || 0) }];
  }
  actief = 0; renders = {}; knipsels = {}; teken(); void ververs();
}
async function slaOp(): Promise<void> {
  if (!huidig) return;
  const r = await fetch(`${API}/opslaan`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: huidig.id, ...instellingen() }) }).then((x) => x.json());
  if (!r.success) throw new Error('save failed: ' + r.error);
  Object.assign(huidig, instellingen(), { plekken: plekken.length > 1 ? JSON.stringify(plekken) : null });
}
let opslaanTimer: ReturnType<typeof setTimeout> | null = null;
let opslaanBezig = false, opslaanNogEens = false;
function planOpslaan(): void {
  if (opslaanTimer) clearTimeout(opslaanTimer);
  opslaanTimer = setTimeout(() => { $('voorbeeld').click(); }, 600);
}
$('voorbeeld').addEventListener('click', async () => {
  if (!huidig) return;
  if (opslaanBezig) { opslaanNogEens = true; return; }
  opslaanBezig = true;
  try { await slaOp(); toon('saved'); }
  catch (e) { toon((e as Error).message, true); }
  finally { opslaanBezig = false; if (opslaanNogEens) { opslaanNogEens = false; planOpslaan(); } }
});
VELDEN.forEach((v) => inp(v).addEventListener('change', planOpslaan));
PLEKVELDEN.forEach((v) => inp(v).addEventListener('change', planOpslaan));

// ---------- projects --------------------------------------------------------------
async function laadProjecten(kiesId?: number | string): Promise<void> {
  const d = await fetch(`${API}/projecten`).then((r) => r.json());
  projecten = d.projecten || [];
  const sel = $('project') as HTMLSelectElement;
  sel.innerHTML = '<option value="">project…</option>' + projecten.map((p) => `<option value="${p.id}">${p.naam}${p.sku ? ' · ' + p.sku : ''}</option>`).join('');
  if (kiesId !== undefined) sel.value = String(kiesId);
}
$('project').addEventListener('change', async () => {
  const sel = $('project') as HTMLSelectElement;
  const p = projecten.find((x) => String(x.id) === sel.value);
  if (!p) return;
  let inst: Record<string, unknown> | null = null;
  try { inst = typeof p.instellingen === 'string' ? JSON.parse(p.instellingen) : p.instellingen; } catch { inst = null; }
  if (!inst) { toon('project could not be read', true); return; }
  if (String(p.template_id) !== String(huidig?.id)) { ($('sjabloon') as HTMLSelectElement).value = String(p.template_id); kiesSjabloon(); }
  if (p.sku && p.sku !== inp('sku').value.trim()) { inp('sku').value = p.sku; await laadProduct(); }
  pasToe(inst);
  toon(`project "${p.naam}" loaded`);
});
$('project-opslaan').addEventListener('click', async () => {
  if (!huidig) return;
  const sel = $('project') as HTMLSelectElement;
  const gekozen = projecten.find((x) => String(x.id) === sel.value);
  const naam = prompt('Project name', gekozen ? gekozen.naam : `${huidig.naam} · ${inp('sku').value.trim()}`);
  if (!naam) return;
  const alsNieuw = !gekozen || naam !== gekozen.naam;
  const r = await fetch(`${API}/project_opslaan`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: alsNieuw ? 0 : gekozen!.id, naam, template_id: huidig.id, sku: inp('sku').value.trim(), instellingen: instellingen() }) }).then((x) => x.json());
  if (!r.success) { toon(r.error, true); return; }
  await laadProjecten(r.id);
  toon('project saved');
});
$('project-weg').addEventListener('click', async () => {
  const sel = $('project') as HTMLSelectElement;
  const p = projecten.find((x) => String(x.id) === sel.value);
  if (!p || !confirm(`Delete project "${p.naam}"?`)) return;
  await fetch(`${API}/project_verwijderen`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: p.id }) });
  await laadProjecten();
  toon('project deleted');
});

// ---------- new template ----------------------------------------------------------
$('nieuw').addEventListener('click', () => {
  const i = document.createElement('input'); i.type = 'file'; i.accept = 'image/png,image/jpeg';
  i.addEventListener('change', async () => {
    const bestand = i.files?.[0]; if (!bestand) return;
    const naam = prompt('Template name', bestand.name.replace(/\.[^.]+$/, ''));
    if (!naam) return;
    const fd = new FormData(); fd.append('bestand', bestand); fd.append('naam', naam);
    toon('uploading…');
    const r = await fetch(`${API}/nieuw`, { method: 'POST', body: fd }).then((x) => x.json());
    if (!r.success) { toon(r.error, true); return; }
    await laadSjablonen(r.id);
    toon('template added');
  });
  i.click();
});

// ---------- link template to products ------------------------------------------------
// A template is switched on for a product when "All products" is ticked, or the
// product's category is ticked, or the product itself. Category ticks also cover
// articles that are added to that category later.
interface CatProduct { sku: string; naam: string }
interface Categorie { naam: string; producten: CatProduct[] }
let catalogus: Categorie[] | null = null;
let kAlles = true;
const kCat = new Set<string>();
const kProd = new Set<string>();
const kOpen = new Set<string>();

const alsLijst = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'string' && v.trim().startsWith('[')) {
    try { const a = JSON.parse(v); return Array.isArray(a) ? a.map(String) : []; } catch { return []; }
  }
  return [];
};
const esc = (tekst: string): string => tekst.replace(/[&<>"']/g,
  (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

function gekoppeldAantal(): number {
  let n = 0;
  for (const c of catalogus || []) for (const p of c.producten) if (kAlles || kCat.has(c.naam) || kProd.has(p.sku)) n++;
  return n;
}

function tekenKoppel(): void {
  if (!catalogus) return;
  const zoek = inp('koppel-zoek').value.trim().toLowerCase();
  const lijstEl = $('koppel-lijst');
  const scroll = lijstEl.scrollTop;
  const totaal = catalogus.reduce((som, c) => som + c.producten.length, 0);
  let html = '';
  for (const c of catalogus) {
    const zichtbaar = !zoek || c.naam.toLowerCase().includes(zoek) ? c.producten
      : c.producten.filter((p) => p.sku.toLowerCase().includes(zoek) || p.naam.toLowerCase().includes(zoek));
    if (!zichtbaar.length) continue;
    const heel = kAlles || kCat.has(c.naam);
    const aantal = heel ? c.producten.length : c.producten.filter((p) => kProd.has(p.sku)).length;
    const open = kOpen.has(c.naam) || (!!zoek && zichtbaar.length <= 40);
    html += `<div class="kcat" data-cat="${esc(c.naam)}"><div class="kcat-kop">`
      + `<button class="kcat-pijl" type="button" title="Show the products">${open ? '▾' : '▸'}</button>`
      + `<label><input type="checkbox" class="kcat-vink"${heel ? ' checked' : ''}${kAlles ? ' disabled' : ''} data-deels="${!heel && aantal > 0 ? 1 : 0}"> <b>${esc(c.naam)}</b></label>`
      + `<span class="faint">${aantal} of ${c.producten.length}</span></div>`;
    if (open) {
      html += '<div class="kcat-lijst">' + zichtbaar.map((p) =>
        `<label class="kprod" title="${esc(p.naam)}"><input type="checkbox" class="kprod-vink" data-sku="${esc(p.sku)}"${heel || kProd.has(p.sku) ? ' checked' : ''}${kAlles ? ' disabled' : ''}>`
        + `<span class="kprod-sku">${esc(p.sku)}</span><span class="kprod-naam">${esc(p.naam)}</span></label>`).join('') + '</div>';
    }
    html += '</div>';
  }
  lijstEl.innerHTML = html || '<div class="hint" style="padding:12px">Nothing found.</div>';
  lijstEl.querySelectorAll<HTMLInputElement>('.kcat-vink[data-deels="1"]').forEach((el) => { el.indeterminate = true; });
  lijstEl.classList.toggle('alles', kAlles);
  lijstEl.scrollTop = scroll;
  $('koppel-telling').textContent = `${gekoppeldAantal()} of ${totaal} products`;
}

function zetCategorie(naam: string, aan: boolean): void {
  const c = (catalogus || []).find((x) => x.naam === naam);
  if (!c) return;
  c.producten.forEach((p) => kProd.delete(p.sku));
  if (aan) kCat.add(naam); else kCat.delete(naam);
}

function zetProduct(sku: string, aan: boolean): void {
  const c = (catalogus || []).find((x) => x.producten.some((p) => p.sku === sku));
  if (!c) return;
  if (kCat.has(c.naam)) {                    // the whole category was ticked: make it single products
    kCat.delete(c.naam);
    c.producten.forEach((p) => kProd.add(p.sku));
  }
  if (aan) kProd.add(sku); else kProd.delete(sku);
  if (c.producten.every((p) => kProd.has(p.sku))) {   // all ticked again: back to the category as a whole
    c.producten.forEach((p) => kProd.delete(p.sku));
    kCat.add(c.naam);
  }
}

$('koppel-open').addEventListener('click', async () => {
  if (!huidig) return;
  if (!catalogus) {
    toon('loading products…');
    const d = await fetch(`${API}/catalogus`).then((r) => r.json());
    if (!d.success) { toon(d.error, true); return; }
    catalogus = d.categorieen as Categorie[];
    toon('');
  }
  kAlles = huidig.koppel_alles !== false;
  kCat.clear(); alsLijst(huidig.koppel_categorieen).forEach((x) => kCat.add(x));
  kProd.clear(); alsLijst(huidig.koppel_producten).forEach((x) => kProd.add(x));
  kOpen.clear();
  inp('koppel-alles').checked = kAlles;
  inp('koppel-zoek').value = '';
  $('koppel-naam').textContent = String(huidig.naam);
  $('koppel').hidden = false;
  tekenKoppel();
});
$('koppel-sluit').addEventListener('click', () => { $('koppel').hidden = true; });
$('koppel').addEventListener('click', (e) => { if (e.target === $('koppel')) $('koppel').hidden = true; });
inp('koppel-alles').addEventListener('change', () => { kAlles = inp('koppel-alles').checked; tekenKoppel(); });
inp('koppel-zoek').addEventListener('input', () => tekenKoppel());
$('koppel-lijst').addEventListener('click', (e) => {
  const pijl = (e.target as HTMLElement).closest('.kcat-pijl');
  if (!pijl) return;
  const naam = (pijl.closest('.kcat') as HTMLElement).dataset.cat || '';
  if (kOpen.has(naam)) kOpen.delete(naam); else kOpen.add(naam);
  tekenKoppel();
});
$('koppel-lijst').addEventListener('change', (e) => {
  const el = e.target as HTMLInputElement;
  if (el.classList.contains('kcat-vink')) {
    zetCategorie((el.closest('.kcat') as HTMLElement).dataset.cat || '', el.checked);
  } else if (el.classList.contains('kprod-vink')) {
    zetProduct(el.dataset.sku || '', el.checked);
  } else return;
  tekenKoppel();
});
$('koppel-opslaan').addEventListener('click', async () => {
  if (!huidig) return;
  const categorieen = Array.from(kCat), producten = Array.from(kProd);
  const r = await fetch(`${API}/koppelingen`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: huidig.id, alles: kAlles, categorieen, producten }) }).then((x) => x.json());
  if (!r.success) { toon(r.error, true); return; }
  huidig.koppel_alles = kAlles; huidig.koppel_categorieen = categorieen; huidig.koppel_producten = producten;
  $('koppel').hidden = true;
  toon(kAlles ? 'template switched on for all products' : `template switched on for ${gekoppeldAantal()} products`);
});

// ---------- sign in: e-mail + one-time code on WhatsApp --------------------------------
// The management routes of the API need a session; the code goes to the phone number
// that belongs to the account. Nothing is loaded until the session is there.
let loginEmail = '';
function loginMelding(tekst: string, fout = false): void {
  const m = $('login-melding'); m.textContent = tekst; m.classList.toggle('fout', fout);
}
function toonLogin(): void {
  $('login').hidden = false;
  $('login-stap1').hidden = false;
  $('login-stap2').hidden = true;
  loginMelding('');
  setTimeout(() => inp('login-email').focus(), 50);
}
async function start(): Promise<void> {
  const d = await fetch(`${API}/auth/me`).then((r) => r.json()).catch(() => ({ success: false }));
  if (!d.success) { toonLogin(); return; }
  $('login').hidden = true;
  $('wie').textContent = String(d.gebruiker.email);
  void laadProjecten();
  void laadSjablonen().then(() => { if (!product) void laadProduct(); });
}
$('login-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  const stap2 = !$('login-stap2').hidden;
  const knop = $<HTMLButtonElement>(stap2 ? 'login-controleer' : 'login-stuur');
  knop.disabled = true;
  try {
    if (!stap2) {
      loginEmail = inp('login-email').value.trim();
      const d = await fetch(`${API}/auth/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail }) }).then((r) => r.json());
      if (!d.success) { loginMelding(d.error || 'The code could not be sent.', true); return; }
      $('login-stap1').hidden = true;
      $('login-stap2').hidden = false;
      loginMelding(d.melding || 'A code has been sent via WhatsApp.');
      inp('login-code').value = '';
      inp('login-code').focus();
    } else {
      const d = await fetch(`${API}/auth/verify`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, code: inp('login-code').value }) }).then((r) => r.json());
      if (!d.success) { loginMelding(d.error || 'Wrong code.', true); inp('login-code').select(); return; }
      await start();
    }
  } catch {
    loginMelding('No connection. Try again.', true);
  } finally {
    knop.disabled = false;
  }
});
$('login-terug').addEventListener('click', () => toonLogin());
$('uitloggen').addEventListener('click', async () => {
  await fetch(`${API}/auth/logout`, { method: 'POST' }).catch(() => undefined);
  location.reload();
});

// A session that ends while you are working: show the sign-in again instead of failing silently.
const echteFetch = window.fetch.bind(window);
window.fetch = async (...a: Parameters<typeof fetch>): Promise<Response> => {
  const r = await echteFetch(...a);
  const url = a[0] instanceof Request ? a[0].url : String(a[0]);
  if (r.status === 401 && url.includes('/api/moodshots/') && !url.includes('/auth/')) toonLogin();
  return r;
};

void start();
